import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { UserAPI } from '../API/user';
import { safeRedirect } from '../utils/redirect';

/**
 * Home page for each role — used as the redirect target when an authenticated
 * user visits a login/signup page.
 */
const ROLE_HOME: Record<string, string> = {
  STUDENT: '/search',
  TUTOR: '/tutor/dashboard',
  ADMIN: '/admin',
  SUPER_ADMIN: '/admin',
};

/**
 * Redirects authenticated users away from login/signup pages.
 *
 * - Unverified users → /verify-email
 * - Verified users → the `redirect` / `forwardPath` query param if present,
 *   otherwise their role-based home.
 */
export function useRedirectIfAuthenticated() {
  const router = useRouter();

  useEffect(() => {
    // Wait for the query string so the redirect param is readable on these
    // statically-optimized pages.
    if (!router.isReady) return;

    UserAPI.userData()
      .then((user) => {
        if (!user.email_approved) {
          router.replace('/verify-email');
          return;
        }

        const home = ROLE_HOME[user.role] ?? '/';
        const raw = router.query.redirect ?? router.query.forwardPath;
        router.replace(safeRedirect(raw, home));
      })
      .catch(() => {
        // Not authenticated — allow access to this page
      });
  }, [router.isReady]); // eslint-disable-line react-hooks/exhaustive-deps
}
